import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import {
  getUrlParamsFetch,
  handleAxiosError,
  notFoundIdHandler,
} from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  PrioridadIncidencia,
  PrioridadIncidenciaPagingRes,
  TipoIncidencia,
  TiposIncidenciaPagingRes,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get, post, put } = erpApi();

/////* Tipos Incidencias -----------------------

///* tanSctack query
export const useFetchTiposIncidencias = (
  params?: GetTiposIncidenciasParams,
  enabled = true
) => {
  return useQuery({
    queryKey: ['tiposIncidencias', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getTiposIncidencias(params);
    },
    enabled,
  });
};

export const useGetTipoIncidencia = (id: string) => {
  return useQuery({
    queryKey: ['tipoIncidencia', id],
    queryFn: async () => {
      return getTipoIncidencia(id);
    },
  });
};

export const useCreateTipoIncidencia = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createTipoIncidencia,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiposIncidencias'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Tipo Incidencia creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateTipoIncidencia = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateTipoIncidencia,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tiposIncidencias'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Tipo Incidencia creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetTiposIncidenciasParams = Partial<TipoIncidencia> & {
  page?: number;
  page_size?: number;

  // filters
  filterByState?: boolean;
};
export type CreateTipoIncidenciaParams = Omit<
  TipoIncidencia,
  'id_tipos_incidencia'
>;
export type UpdateTipoIncidenciaParams = {
  id: string;
  data: CreateTipoIncidenciaParams;
};

export const getTiposIncidencias = async (
  params?: GetTiposIncidenciasParams
) => {
  const stateParams = { ...params };

  // filterByState
  if (stateParams.filterByState === false && stateParams.estado === undefined) {
    delete stateParams.estado;
  } else if (stateParams.filterByState !== false) {
    stateParams.estado = true;
  }
  delete stateParams.filterByState;

  const queryParams = getUrlParamsFetch(stateParams);

  return get<TiposIncidenciaPagingRes>(
    `${urlBackend}api/v1/incidencia/?${queryParams}`,
    true
  );
};

export const getTipoIncidencia = async (id: string) => {
  try {
    return await get<TipoIncidencia>(
      `${urlBackend}api/v1/incidencia/${id}/`,
      true
    );
  } catch (error) {
    handleAxiosError(error);
  }
};

export const createTipoIncidencia = async (
  data: CreateTipoIncidenciaParams
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<TipoIncidencia>(`${urlBackend}api/v1/incidencia/`, data, true);
};

export const updateTipoIncidencia = async ({
  id,
  data,
}: UpdateTipoIncidenciaParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<TipoIncidencia>(
    `${urlBackend}api/v1/incidencia/${id}/`,
    data,
    true
  );
};

/////* Prioridad Incidencia -----------------------

///* tanSctack query
export const useFetchPrioridadIncidencias = (
  params?: GetPrioridadIncidenciasParams
) => {
  return useQuery({
    queryKey: ['prioridadIncidencias', ...Object.values(params || {})],
    queryFn: async () => {
      return getPrioridadIncidencias(params);
    },
  });
};

export const useGetPrioridadIncidencia = (id: string) => {
  return useQuery({
    queryKey: ['prioridadIncidencia', id],
    queryFn: async () => {
      return getPrioridadIncidencia(id);
    },
  });
};

export const useCreatePrioridadIncidencia = (
  navigate: NavigateFunction,
  returnUrl: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPrioridadIncidencia,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prioridadIncidencias'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

///* axios
export type GetPrioridadIncidenciasParams = {
  page?: number;
  page_size?: number;

  // filters
  nombre?: string;
  descripcion?: string;
  estado?: boolean;
};
export type CreatePrioridadIncidenciaParams = Omit<
  PrioridadIncidencia,
  'id_prioridad_incidencia'
>;
export type UpdatePrioridadIncidenciaParams = {
  id: string;
  data: CreatePrioridadIncidenciaParams;
};

export const getPrioridadIncidencias = async (
  params?: GetPrioridadIncidenciasParams
) => {
  const queryParams = getUrlParamsFetch(params || {});
  return get<PrioridadIncidenciaPagingRes>(
    `${urlBackend}api/v1/prioridadincidencia/?${queryParams}`,
    true
  );
};

export const getPrioridadIncidencia = async (id: string) => {
  const res = await get<PrioridadIncidencia>(
    `${urlBackend}api/v1/prioridadincidencia/${id}/`,
    true
  );

  notFoundIdHandler(res);
  return res;
};

export const createPrioridadIncidencia = async (
  data: CreatePrioridadIncidenciaParams
) => {
  const setAlert = useUiStore.getState().setAlert;

  const res = await post<PrioridadIncidencia>(
    `${urlBackend}api/v1/prioridadincidencia/`,
    data,
    true
  );
  setAlert(true, res.message, res.status);

  return res;
};

export const updatePrioridadIncidencia = async ({
  id,
  data,
}: UpdatePrioridadIncidenciaParams) => {
  const setAlert = useUiStore.getState().setAlert;

  const res = await put<PrioridadIncidencia>(
    `${urlBackend}api/v1/prioridadincidencia/${id}/`,
    data,
    true
  );
  setAlert(true, res.message, res.status);

  return res;
};
